import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import './ui-polish.css';
import { Routes, Route, Link, NavLink, useNavigate } from 'react-router-dom';
import Register from './components/Register';
import Login from './components/Login';
import Landing from './components/Landing';
import ProtectedRoute from './components/ProtectedRoute';
import RecipeExtractor from './components/RecipeExtractor';
import SavedRecipes from './components/SavedRecipes';
import { jwtDecode } from 'jwt-decode';

import Profile from './components/Profile';
import SavedRecipeDetail from './components/SavedRecipeDetail';
import OnboardingModal from './components/OnboardingModal';
import AdminAds from './components/AdminAds';
import AdminPanel from './components/AdminPanel';
import AdminUsers from './components/AdminUsers';
import AdminPayments from './components/AdminPayments';
import AdminLayout from './components/AdminLayout';
import { useUser } from './context/UserContext';

function App() {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isDarkMode, setIsDarkMode] = useState(() => {
        const savedMode = localStorage.getItem('darkMode');
        return savedMode === 'true' ? true : false;
    });
    const navigate = useNavigate();
    const { refreshUser, resetUser, user } = useUser();

    const handleLogout = useCallback(() => {
        localStorage.removeItem('token');
        setIsAuthenticated(false);
        resetUser();
        navigate('/');
    }, [navigate, resetUser]);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const decodedToken = jwtDecode(token);
                if (decodedToken.exp * 1000 < Date.now()) {
                    handleLogout();
                } else {
                    setIsAuthenticated(true);
                }
            } catch (error) {
                handleLogout();
            }
        }
    }, [handleLogout]);

    useEffect(() => {
        let logoutTimer;
        if (isAuthenticated) {
            const token = localStorage.getItem('token');
            if (token) {
                try {
                    const decodedToken = jwtDecode(token);
                    const currentTime = Date.now();
                    const expiresIn = (decodedToken.exp * 1000) - currentTime;

                    if (expiresIn > 0) {
                        logoutTimer = setTimeout(() => {
                            handleLogout();
                        }, expiresIn);
                    } else {
                        handleLogout();
                    }
                } catch (error) {
                    handleLogout();
                }
            }
        }
        return () => {
            if (logoutTimer) {
                clearTimeout(logoutTimer);
            }
        };
    }, [handleLogout, isAuthenticated]);


    useEffect(() => {
        localStorage.setItem('darkMode', isDarkMode);
        if (isDarkMode) {
            document.body.classList.add('dark-mode');
        } else {
            document.body.classList.remove('dark-mode');
        }
    }, [isDarkMode]);

    const handleLogin = (token) => {
        localStorage.setItem('token', token);
        setIsAuthenticated(true);
        refreshUser();
        navigate('/app');
    };

    const toggleDarkMode = () => {
        setIsDarkMode(prevMode => !prevMode);
    };

    // Determine admin access in client (also enforced server-side). Use REACT_APP_ADMIN_EMAILS comma-separated.
    const adminEmails = (process.env.REACT_APP_ADMIN_EMAILS || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
    const showAdmin = !!(user && (user.admin || adminEmails.includes((user.email || '').toLowerCase())));

    return (
        <div className="app-shell">
            <a className="skip-link" href="#main">Skip to content</a>
            <header className="app-header">
                <nav className="navbar navbar-expand-lg app-navbar">
                    <div className="container-fluid">
                        <Link className="navbar-brand" to={isAuthenticated ? '/app' : '/'}>
                            RecipeCore
                        </Link>
                        <button
                            className="navbar-toggler"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#appNavBar"
                            aria-controls="appNavBar"
                            aria-expanded="false"
                            aria-label="Toggle navigation"
                        >
                            <span className="navbar-toggler-icon"></span>
                        </button>
                        <div className="collapse navbar-collapse" id="appNavBar">
                            <ul className="navbar-nav ms-auto align-items-lg-center">
                                {isAuthenticated ? (
                                    <>
                                        {showAdmin && (
                                            <li className="nav-item">
                                                <NavLink
                                                    to="/admin"
                                                    className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                                                >
                                                    Admin
                                                </NavLink>
                                            </li>
                                        )}

                                        <li className="nav-item">
                                            <NavLink
                                                to="/app"
                                                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                                            >
                                                Workspace
                                            </NavLink>
                                        </li>

                                        <li className="nav-item">
                                            <NavLink
                                                to="/saved-recipes"
                                                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                                            >
                                                Saved recipes
                                            </NavLink>
                                        </li>

                                        <li className="nav-item">
                                            <NavLink
                                                to="/profile"
                                                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                                            >
                                                Profile
                                            </NavLink>
                                        </li>

                                        {showAdmin && (
                                            <li className="nav-item">
                                                <NavLink
                                                    to="/admin/ads"
                                                    className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                                                >
                                                    Ads
                                                </NavLink>
                                            </li>
                                        )}

                                        <li className="nav-item">
                                            <button className="btn btn-link nav-link" type="button" onClick={handleLogout}>
                                                Logout
                                            </button>
                                        </li>
                                    </>
                                ) : (
                                    <>
                                        <li className="nav-item">
                                            <NavLink
                                                to="/"
                                                end
                                                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                                            >
                                                Overview
                                            </NavLink>
                                        </li>
                                        <li className="nav-item">
                                            <NavLink
                                                to="/register"
                                                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                                            >
                                                Register
                                            </NavLink>
                                        </li>
                                        <li className="nav-item">
                                            <NavLink
                                                to="/login"
                                                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                                            >
                                                Login
                                            </NavLink>
                                        </li>
                                    </>
                                )}
                                <li className="nav-item ms-lg-3 mt-3 mt-lg-0">
                                    <button
                                        type="button"
                                        className={`theme-toggle ${isDarkMode ? 'is-dark' : ''}`}
                                        onClick={toggleDarkMode}
                                        aria-pressed={isDarkMode}
                                    >
                                        <span className="theme-toggle-track">
                                            <span className="theme-toggle-thumb"></span>
                                        </span>
                                        <span className="theme-toggle-label">
                                            {isDarkMode ? 'Dark' : 'Light'} mode
                                        </span>
                                    </button>
                                </li>
                            </ul>
                        </div>
                    </div>
                </nav>
            </header>
            <OnboardingModal />

            <main id="main" className="app-main">
                <Routes>
                    <Route path="/" element={<Landing />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/login" element={<Login onLogin={handleLogin} />} />
                    <Route path="/app" element={<ProtectedRoute><RecipeExtractor /></ProtectedRoute>} />
                    <Route path="/profile" element={<ProtectedRoute><Profile isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} onLogout={handleLogout} /></ProtectedRoute>} />
                    <Route path="/saved-recipes" element={<ProtectedRoute><SavedRecipes /></ProtectedRoute>} />
                    <Route path="/recipes/:id" element={<ProtectedRoute><SavedRecipeDetail /></ProtectedRoute>} />
                    <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
                        <Route index element={<AdminPanel />} />
                        <Route path="ads" element={<AdminAds />} />
                        <Route path="users" element={<AdminUsers />} />
                        <Route path="payments" element={<AdminPayments />} />
                    </Route>
                </Routes>
            </main>

            <footer className="app-footer">
                <div className="footer-card">
                    Crafted for cooks who want signal, not scroll.
                </div>
            </footer>
        </div>
    );
}

export default App;
